import * as React from 'react';

import { useContractIdsForPrograms } from 'bundles/enterprise-admin-learners/hooks/useContractIdsForPrograms';
import { useContractsByOrgId } from 'bundles/enterprise-admin-program-catalog/hooks/useContractsByOrgId';

export type UseContractOptionsForInviteOptions = {
  /**
   * True while the async source of `scopingProgramIds` (e.g. manageable groups)
   * hasn't resolved yet. Kept separate from `scopingProgramIds` itself because
   * "no scoping needed" and "scoping needed, but we don't know the groups yet"
   * both need `scopingProgramIds` to look empty/undefined at some point, and
   * only one of those should resolve immediately.
   */
  scopingLoading?: boolean;
};

/**
 * The contract options for the invite flow's contract picker: the full org-wide
 * list when `scopingProgramIds` is `undefined` (org admin, unscoped), or narrowed
 * to contracts with a roster under any of `scopingProgramIds` otherwise (invite
 * started from within a group, or by a Group admin scoped to their manageable
 * groups). See `useContractIdsForPrograms` for why `scopingLoading` can't be
 * folded into `scopingProgramIds`.
 */
export const useContractOptionsForInvite = (
  scopingProgramIds: string[] | undefined,
  { scopingLoading = false }: UseContractOptionsForInviteOptions = {}
) => {
  const { contractOptions: orgWideOptions, loading: orgLoading, error: orgError } = useContractsByOrgId();
  const { contractIds, loading: idsLoading, error: idsError } = useContractIdsForPrograms(scopingProgramIds ?? []);

  const contractOptions = React.useMemo(() => {
    if (scopingProgramIds === undefined) return orgWideOptions;
    if (scopingLoading || !orgWideOptions || !contractIds) return undefined;
    return orgWideOptions.filter((c) => contractIds.has(c.id));
  }, [scopingProgramIds, scopingLoading, orgWideOptions, contractIds]);

  return {
    contractOptions,
    loading: orgLoading || idsLoading || scopingLoading,
    error: orgError ?? idsError,
  };
};

export default useContractOptionsForInvite;
